import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { products } from '@/mocks/products';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const product = products.find((p) => p.id === id);
  const [quantity, setQuantity] = useState(1);

  if (!product) {
    return (
      <>
        <Navbar />
        <main className="pt-28 md:pt-36 pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">
              Product Not Found
            </h1>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors"
            >
              Back to Shop
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedProducts = products.filter((p) => p.id !== id).slice(0, 3);

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-28 md:pt-36 pb-10 md:pb-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
              <div className="aspect-square rounded-lg overflow-hidden bg-background-100 border border-background-200/60">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="flex flex-col justify-center">
                <p className="text-primary-500 font-label text-sm tracking-[0.15em] uppercase mb-2">
                  {product.category}
                </p>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                  {product.name}
                </h1>

                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i
                        key={s}
                        className={`ri-star-fill text-sm ${
                          s <= Math.round(product.rating)
                            ? 'text-accent-500'
                            : 'text-background-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-foreground-600 text-sm">
                    {product.rating} ({product.reviews} reviews)
                  </span>
                </div>

                <p className="text-foreground-700 text-base leading-relaxed mb-6">
                  {product.description}
                </p>

                <div className="flex items-center gap-4 mb-6">
                  <span className="font-label text-2xl md:text-3xl font-semibold text-primary-600">
                    {product.price}
                  </span>
                  <span className="text-foreground-500 text-sm">
                    / {product.weight}
                  </span>
                </div>

                {product.badge && (
                  <span className="inline-block self-start px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full mb-6">
                    {product.badge}
                  </span>
                )}

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
                  <div className="flex items-center border border-background-300/60 rounded-md overflow-hidden">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 flex items-center justify-center text-foreground-700 hover:bg-background-100 transition-colors"
                      aria-label="Decrease quantity"
                    >
                      <i className="ri-subtract-line" />
                    </button>
                    <span className="w-12 h-10 flex items-center justify-center text-foreground-900 font-medium text-sm border-x border-background-300/60">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 flex items-center justify-center text-foreground-700 hover:bg-background-100 transition-colors"
                      aria-label="Increase quantity"
                    >
                      <i className="ri-add-line" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="px-6 py-3.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap">
                    Add to Cart
                  </button>
                  <button className="px-6 py-3.5 border border-primary-500 text-primary-600 text-sm font-medium rounded-md hover:bg-primary-500 hover:text-background-50 transition-colors whitespace-nowrap">
                    Buy Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedProducts.length > 0 && (
          <section className="py-12 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-8">
                You May Also Like
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {relatedProducts.map((p) => (
                  <Link
                    key={p.id}
                    to={`/product/${p.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-300/50 transition-all duration-300"
                  >
                    <div className="aspect-square overflow-hidden bg-background-100">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-1">
                        {p.name}
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="font-label text-primary-600 font-semibold text-sm">
                          {p.price}
                        </span>
                        <span className="text-foreground-500 text-xs">
                          {p.weight}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}